import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { launchModal } from '@domain-group/javascript-utils'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import * as savedArtworkPageActions from '@redux/modules/savedArtworkPage'

import defaultFormState, { requiredFields } from '@models/savedArtworkPage'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type {
  AgencyModel,
  SavedArtworkModel,
  SavedArtworkPageModel,
  SavedArtworkPageRequestOptions,
  UserModel,
} from '@models/types'

import PageContext from '@contexts/pageContext'

const stringifyObjectKeys: (keyof SavedArtworkPageModel)[] = ['data']

type GenerateUrlsOptions = {
  prefixRoute?: string,
}

export const generateUrls = (savedArtworkPage?: Partial<SavedArtworkPageModel>, options: GenerateUrlsOptions = {}) => {
  const { id } = savedArtworkPage || {}
  const { prefixRoute = 'systemManager' } = options || {}

  return {
    savedArtworksIndexUrl: `#/${prefixRoute}/savedArtworks`,
    editSavedArtworkPageUrl: `#/${prefixRoute}/savedArtworkPages/${id}/edit/`,
  }
}

type CreateSavedArtworkPageParams = {
  dispatch: AppDispatch,
  requestOptions?: SavedArtworkPageRequestOptions,
  savedArtworkPageParams: Partial<SavedArtworkPageModel>,
}

const createSavedArtworkPage = (params: CreateSavedArtworkPageParams) => {
  const { dispatch, savedArtworkPageParams, requestOptions } = params
  const { createSavedArtworkPage: createFn } = savedArtworkPageActions

  const updatedParams = mergeAndStringifyEntityObjects({}, savedArtworkPageParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions))
}

type UpdateSavedArtworkPageParams = {
  dispatch: AppDispatch,
  requestOptions?: SavedArtworkPageRequestOptions,
  savedArtworkPage: SavedArtworkPageModel,
  savedArtworkPageParams: Partial<SavedArtworkPageModel>,
}

const updateSavedArtworkPage = (params: UpdateSavedArtworkPageParams) => {
  const {
    dispatch, savedArtworkPage, savedArtworkPageParams, requestOptions,
  } = params
  const { updateSavedArtworkPage: updateFn } = savedArtworkPageActions

  const updatedParams = mergeAndStringifyEntityObjects(savedArtworkPage, savedArtworkPageParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteSavedArtworkPageParams = {
  dispatch: AppDispatch,
  savedArtworkPage: DeleteParams<SavedArtworkPageModel>,
}

const deleteSavedArtworkPage = (params: DeleteSavedArtworkPageParams) => {
  const { dispatch, savedArtworkPage } = params
  const { deleteSavedArtworkPage: deleteFn } = savedArtworkPageActions

  return dispatch(deleteFn(savedArtworkPage))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useSavedArtworkPageForm(
  savedArtworkPage: Partial<SavedArtworkPageModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const savedArtworkPageForm = useForm(
    defaultFormState,
    { entity: savedArtworkPage, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [savedArtworkPage.id, savedArtworkPage.cache_key],
  )

  return {
    ...savedArtworkPageForm,
  }
}

// @ts-expect-error TS7006
export function useRelations(savedArtworkPage) {
  const { agency_id, user_id, saved_artwork_id } = savedArtworkPage

  const { agencies, users, savedArtworks } = useSelector(reduxState => reduxState.entities)

  const agency: Partial<AgencyModel> = agencies[agency_id] || {}
  const savedArtwork: Partial<SavedArtworkModel> = savedArtworks[saved_artwork_id] || {}
  const user: Partial<UserModel> = users[user_id] || {}

  return {
    agency,
    savedArtwork,
    user,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: SavedArtworkPageRequestOptions,
}

function useSavedArtworkPage(initEntity: Partial<SavedArtworkPageModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: savedArtworkPage }: { entity: SavedArtworkPageModel} = useLatestEntity(initEntity, 'savedArtworkPages')
  const { id } = savedArtworkPage

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { agency, user, savedArtwork } = useRelations(savedArtworkPage)

  useReduxAction(
    'savedArtworkPages',
    'loadSavedArtworkPage',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.savedArtworkPages)

  return {
    agency,
    callbacks: {
      createSavedArtworkPage: (savedArtworkPageParams: Partial<SavedArtworkPageModel>, entityOptions?: SavedArtworkPageRequestOptions) => (
        createSavedArtworkPage({ savedArtworkPageParams, dispatch, requestOptions: entityOptions })
      ),
      deleteSavedArtworkPage: () => deleteSavedArtworkPage({ dispatch, savedArtworkPage }),
      launchManageSavedArtworkPageModal: () => launchModal({
        callbacks,
        modalKey: 'ManageSavedArtworkPageModal',
        payload: { savedArtworkPage },
      }),
      updateSavedArtworkPage: (savedArtworkPageParams: Partial<SavedArtworkPageModel>, entityOptions?: SavedArtworkPageRequestOptions) => (
        updateSavedArtworkPage({
          savedArtworkPage, savedArtworkPageParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    loading: loading && !loadedIds.includes(savedArtworkPage.id),
    savedArtwork,
    savedArtworkPage,
    updating,
    urls: generateUrls(savedArtworkPage),
    user,
  }
}

export default useSavedArtworkPage
